import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, styled, useTheme } from '@mui/material';
import { IconInfoCircleFilled, IconLink, IconPhoto } from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import {
  ConversationStartedPayload,
  CreateTicketPayload,
  GenericMessagePayload,
  GenericToolPayload,
  InitialIntentMessagePayload,
  IntentPredictionPayload,
  IntentRoutingPayload,
  SearchHelpCenterArticlesPayload,
  WorkflowExecutionPayload,
  WorkflowWidgetBatchPayload,
  WorkflowWidgetResponsePayload,
} from '../../type';
import deepParseJson from './deepParseJson';
import warningIcon from 'src/assets/images/circular-warning-icon.svg';
import { ColorMode } from 'src/components/app/types';
import {
  getMCPToolName,
  getNameForTool,
} from 'src/pages/workflow-builder-edit/autonomous-agent/action-drawer/helpers';
import { useResolvedActionName } from 'src/services/action-builder/useResolvedActionName';
import { setVoicePreviewConversationId } from 'src/slices/workflow-preview/workflowPreviewSlice';
import { useAppDispatch } from 'src/store/hooks';

export const ConversationIDMessage = ({
  payload,
}: {
  payload: ConversationStartedPayload;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const theme = useTheme();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setVoicePreviewConversationId(payload.conversation_id));
  }, [dispatch, payload.conversation_id]);

  return (
    <ExecutionMessageContainer>
      <Row>
        <Typography color={theme.palette.colors.grey[100]} variant='font14'>
          {t('previewExecutionMessage.conversationId')}
        </Typography>

        <Typography color={theme.palette.colors.blue[300]} variant='font14'>
          {payload.conversation_id}
        </Typography>
      </Row>
    </ExecutionMessageContainer>
  );
};

export const InitialIntentMessage = ({
  payload,
}: {
  payload: InitialIntentMessagePayload;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const theme = useTheme();

  return (
    <ExecutionMessageContainer>
      <Row>
        <Typography color={theme.palette.colors.grey[100]} variant='font14'>
          {t('previewExecutionMessage.executingInitialIntent')}
        </Typography>

        <Typography color={theme.palette.colors.blue[300]} variant='font14'>
          {payload.intent_title}
        </Typography>
      </Row>
    </ExecutionMessageContainer>
  );
};

export const GenericMessage = ({
  payload,
}: {
  payload: GenericMessagePayload;
}) => {
  const theme = useTheme();

  return (
    <ExecutionMessageContainer>
      <Row>
        {payload.title && (
          <Typography color={theme.palette.colors.grey[100]} variant='font14'>
            {payload.title}
          </Typography>
        )}

        <Typography color={theme.palette.colors.blue[300]} variant='font14'>
          {payload.message}
        </Typography>
      </Row>
    </ExecutionMessageContainer>
  );
};

export const IntentPredictionMessage = ({
  payload,
}: {
  payload: IntentPredictionPayload;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const theme = useTheme();

  if (payload.intent === 'Knowledge Retrieval') {
    return (
      <ExecutionMessageContainer>
        <Row>
          <img alt='' src={warningIcon} />
          <Typography color={theme.palette.colors.grey[100]} variant='font14'>
            {t('previewExecutionMessage.intentPrediction.noIntentPredicted')}
          </Typography>
        </Row>
        <Typography color={theme.palette.colors.grey[300]} variant='font14'>
          {t(
            'previewExecutionMessage.intentPrediction.enteringKnowledgeRetrieval',
          )}
        </Typography>
      </ExecutionMessageContainer>
    );
  }
  return (
    <ExecutionMessageContainer>
      <Typography color={theme.palette.colors.grey[100]} variant='font14'>
        {t('previewExecutionMessage.intentPrediction.predictedIntent')}
      </Typography>

      <Typography color={theme.palette.colors.blue[300]} variant='font14'>
        {payload.intent}
      </Typography>
    </ExecutionMessageContainer>
  );
};

export const SearchHelpCenterArticlesMessage = ({
  payload,
}: {
  payload: SearchHelpCenterArticlesPayload;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const theme = useTheme();

  if (payload.articles && payload.articles.length > 0) {
    return (
      <ExecutionMessageContainer>
        <Row>
          <IconInfoCircleFilled
            size={20}
            style={{
              color: theme.palette.colors.green[500],
              fill: theme.palette.colors.green[500],
            }}
          />
          <Typography color={'white'} variant='font14'>
            {t(
              'previewExecutionMessage.searchHelpCenterArticles.generatedAnswerFrom',
            )}
          </Typography>
        </Row>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            mt: '4px',
          }}
        >
          {payload.articles.map(article => {
            return (
              <Box
                key={article.title}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Row>
                  <Box
                    sx={{
                      display: 'flex',
                      maxHeight: '20px',
                      minWidth: '50px',
                    }}
                  >
                    <Typography
                      color={theme.palette.colors.grey[100]}
                      variant='font14'
                    >
                      {t(
                        'previewExecutionMessage.searchHelpCenterArticles.title',
                      )}
                    </Typography>
                  </Box>

                  <Typography
                    color={theme.palette.colors.grey[100]}
                    variant='font14'
                  >
                    {article.title}
                  </Typography>
                </Row>
                <Row>
                  <Box
                    sx={{
                      display: 'flex',
                      maxHeight: '20px',
                      minWidth: '50px',
                    }}
                  >
                    <Typography
                      color={theme.palette.colors.grey[100]}
                      variant='font14'
                    >
                      {t(
                        'previewExecutionMessage.searchHelpCenterArticles.link',
                      )}
                    </Typography>
                  </Box>

                  <Typography
                    color={theme.palette.colors.blue[300]}
                    variant='font14'
                  >
                    {article.link}
                  </Typography>
                </Row>
              </Box>
            );
          })}
        </Box>
      </ExecutionMessageContainer>
    );
  }

  return (
    <ExecutionMessageContainer>
      <Row>
        <IconInfoCircleFilled
          size={20}
          style={{
            color: theme.palette.colors.green[500],
            fill: theme.palette.colors.green[500],
          }}
        />
        <Typography color={'white'} variant='font14'>
          {t(
            'previewExecutionMessage.searchHelpCenterArticles.noArticlesFound',
          )}
        </Typography>
      </Row>
    </ExecutionMessageContainer>
  );
};

export const CreateTicketMessage = ({
  payload,
  type,
}: {
  payload: CreateTicketPayload;
  type: 'create_zendesk_ticket' | 'create_intercom_ticket';
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const theme = useTheme();

  return (
    <ExecutionMessageContainer>
      <Row>
        <IconInfoCircleFilled
          size={20}
          style={{
            color: theme.palette.colors.green[500],
            fill: theme.palette.colors.green[500],
          }}
        />
        <Typography color={'white'} variant='font14'>
          {t('previewExecutionMessage.createTicket.executedResult', {
            toolName: getNameForTool(type),
          })}
        </Typography>
      </Row>

      <Row>
        <Typography color={'white'} variant='font14'>
          {t('previewExecutionMessage.createTicket.status')}
        </Typography>

        <Typography color={theme.palette.colors.blue[300]} variant='font14'>
          {payload.status}
        </Typography>
      </Row>

      <Row>
        <Typography color={'white'} variant='font14'>
          {t('previewExecutionMessage.createTicket.ticketId')}
        </Typography>

        <Typography color={theme.palette.colors.blue[300]} variant='font14'>
          {payload.ticket_id}
        </Typography>
      </Row>
    </ExecutionMessageContainer>
  );
};

export const GenericToolMessage = ({
  payload,
  theme = 'dark',
}: {
  payload: GenericToolPayload;
  theme?: ColorMode;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const muiTheme = useTheme();
  const palette = muiTheme.palette.colors;
  const toolName = useResolvedActionName(payload.tool_id, payload.tool_name);

  const colorSets = {
    dark: {
      background: palette.grey[900],
      border: palette.grey[700],
      info: palette.blue[300],
      secondaryText: palette.grey[100],
      success: palette.green[500],
      text: 'white',
    },
    light: {
      background: palette.grey[100],
      border: palette.grey[300],
      info: palette.blue[500],
      secondaryText: palette.grey[700],
      success: palette.green[500],
      text: palette.grey[900],
    },
  };
  const colors = colorSets[theme];

  return (
    <ExecutionMessageContainer
      bgcolor={colors.background}
      border={`1px solid ${colors.border}`}
      colorTheme={theme}
    >
      <Row>
        <IconInfoCircleFilled
          size={20}
          style={{
            color: colors.success,
            fill: colors.success,
          }}
        />
        <Typography color={colors.text} variant='font14'>
          {t('previewExecutionMessage.genericTool.executedTool')}
        </Typography>
        <Typography color={colors.info} variant='font14'>
          {t('previewExecutionMessage.genericTool.toolNameWithPeriod', {
            toolName: getMCPToolName(toolName),
          })}
        </Typography>
      </Row>

      <Typography color={colors.secondaryText} variant='font14'>
        <pre
          style={{
            margin: 0,
            maxHeight: '400px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            wordWrap: 'break-word',
          }}
        >
          <span style={{ color: colors.info }}>
            {t('previewLogs.apiStep.inputLabel')}
          </span>
          {JSON.stringify(payload.input, null, 2)}
        </pre>
      </Typography>

      <Typography color={colors.secondaryText} variant='font14'>
        <pre
          style={{
            margin: 0,
            maxHeight: '400px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            wordWrap: 'break-word',
          }}
        >
          <span style={{ color: colors.info }}>
            {t('previewLogs.apiStep.outputLabel')}
          </span>
          {JSON.stringify(deepParseJson(payload.output), null, 2)}
        </pre>
      </Typography>

      {payload.reasoning && (
        <Typography color={colors.secondaryText} variant='font14'>
          <pre
            style={{
              margin: 0,
              maxHeight: '400px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
            }}
          >
            <span style={{ color: colors.info }}>
              {t('previewLogs.intentPrediction.reasoningLabel')}
            </span>
            {payload.reasoning}
          </pre>
        </Typography>
      )}
    </ExecutionMessageContainer>
  );
};

export const IntentRoutingMessage = ({
  payload,
}: {
  payload: IntentRoutingPayload;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const theme = useTheme();

  return (
    <ExecutionMessageContainer>
      <Row>
        <IconInfoCircleFilled
          size={20}
          style={{
            color: theme.palette.colors.green[500],
            fill: theme.palette.colors.green[500],
          }}
        />
        <Typography color={theme.palette.colors.grey[100]} variant='font14'>
          {t('previewExecutionMessage.routingToWorkflow')}
        </Typography>
      </Row>

      <Typography color={theme.palette.colors.blue[300]} variant='font14'>
        {payload.intent_title}
      </Typography>
    </ExecutionMessageContainer>
  );
};

export const WorkflowExecutionMessage = ({
  payload,
}: {
  payload: WorkflowExecutionPayload;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const theme = useTheme();
  const palette = theme.palette.colors;

  return (
    <ExecutionMessageContainer
      bgcolor={palette.grey[900]}
      border={`1px solid ${palette.grey[700]}`}
    >
      <Row>
        <IconInfoCircleFilled
          size={20}
          style={{
            color: palette.green[500],
            fill: palette.green[500],
          }}
        />
        <Typography color='white' variant='font14'>
          {t('previewExecutionMessage.executedBuildingBlock')}
        </Typography>
        <Typography color={palette.blue[300]} variant='font14'>
          {payload.workflow_title}
        </Typography>
      </Row>
    </ExecutionMessageContainer>
  );
};

const ExecutionMessageContainer = styled(Box)<{
  colorTheme?: 'dark' | 'light';
}>`
  background-color: ${props =>
    props.colorTheme === 'light'
      ? undefined
      : props.theme.palette.colors.grey[900]};
  border: ${props =>
    props.colorTheme === 'light'
      ? undefined
      : `1px solid ${props.theme.palette.colors.grey[700]}`};
  border-radius: 12px;
  font-family: Fira Code !important;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Row = styled('div')`
  display: flex;
  gap: 8px;
  word-break: break-word;
  align-items: flex-start;
`;

const WidgetChipsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const WidgetBatchContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const getCardTitle = (card: {
  displayable_dynamic_sections: Array<{
    components: Array<{ label?: string; value: string }>;
    type: string;
  }>;
  value: string;
}): string => {
  for (const section of card.displayable_dynamic_sections) {
    if (section.type === 'title' && section.components[0]) {
      const comp = section.components[0];
      return [comp.label, comp.value].filter(Boolean).join(' ');
    }
  }
  return card.value;
};

export const WorkflowWidgetBatchMessage = ({
  payload,
}: {
  payload: WorkflowWidgetBatchPayload;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const theme = useTheme();
  const palette = theme.palette.colors;

  return (
    <WidgetBatchContainer>
      {payload.components.map((component, index) => {
        switch (component.component_type) {
          case 'image': {
            const images = (component.component_fields?.images ?? []) as Array<{
              description?: string;
              url: string;
            }>;
            const firstImage = images[0];
            return (
              <ExecutionMessageContainer key={index}>
                <Row>
                  <IconPhoto
                    size={20}
                    style={{ color: palette.blue[300], flexShrink: 0 }}
                  />
                  <Typography color='white' variant='font14'>
                    {t('previewExecutionMessage.sharedImage')}
                  </Typography>
                </Row>
                {firstImage && (
                  <img
                    alt={
                      firstImage.description ||
                      t('previewExecutionMessage.sharedImage')
                    }
                    src={firstImage.url}
                    style={{
                      borderRadius: '8px',
                      marginTop: '4px',
                      maxHeight: '120px',
                      maxWidth: '100%',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </ExecutionMessageContainer>
            );
          }
          case 'article': {
            const articles = (component.component_fields?.knowledge_articles ??
              []) as Array<{
              link: string;
              title?: string;
            }>;
            const firstArticle = articles[0];
            return (
              <ExecutionMessageContainer key={index}>
                <Row>
                  <IconLink
                    size={20}
                    style={{ color: palette.blue[300], flexShrink: 0 }}
                  />
                  <Typography color='white' variant='font14'>
                    {t('previewExecutionMessage.sharedLink')}
                  </Typography>
                </Row>
                {firstArticle && (
                  <Typography color={palette.blue[300]} variant='font14'>
                    {firstArticle.title || firstArticle.link}
                  </Typography>
                )}
              </ExecutionMessageContainer>
            );
          }
          case 'buttons': {
            const options = (component.component_fields?.options ??
              []) as Array<{
              display_name: string;
              value: string;
            }>;
            return (
              <ExecutionMessageContainer key={index}>
                <Row>
                  <IconInfoCircleFilled
                    size={20}
                    style={{ color: palette.blue[300], flexShrink: 0 }}
                  />
                  <Typography color='white' variant='font14'>
                    {t('previewExecutionMessage.triggeredComponent.button')}
                  </Typography>
                </Row>
                {options.length > 0 && (
                  <WidgetChipsContainer>
                    {options.map(option => (
                      <Chip
                        key={option.value}
                        label={option.display_name}
                        sx={{
                          border: `1px solid ${palette.grey[500]}`,
                          color: 'white',
                        }}
                        variant='outlined'
                      />
                    ))}
                  </WidgetChipsContainer>
                )}
              </ExecutionMessageContainer>
            );
          }
          case 'form': {
            const formFields = (component.component_fields?.form_fields ??
              []) as Array<{
              placeholder: string;
            }>;
            return (
              <ExecutionMessageContainer key={index}>
                <Row>
                  <IconInfoCircleFilled
                    size={20}
                    style={{ color: palette.blue[300], flexShrink: 0 }}
                  />
                  <Typography color='white' variant='font14'>
                    {t('previewExecutionMessage.triggeredComponent.form')}
                  </Typography>
                </Row>
                {formFields.length > 0 && (
                  <Typography color={palette.grey[300]} variant='font14'>
                    {t('previewExecutionMessage.triggeredComponent.fields', {
                      fields: formFields.map(f => f.placeholder).join(', '),
                    })}
                  </Typography>
                )}
              </ExecutionMessageContainer>
            );
          }
          case 'dynamic_card': {
            const cards = (component.component_fields?.cards ?? []) as Array<{
              displayable_dynamic_sections: Array<{
                components: Array<{ label?: string; value: string }>;
                type: string;
              }>;
              value: string;
            }>;
            return (
              <ExecutionMessageContainer key={index}>
                <Row>
                  <IconInfoCircleFilled
                    size={20}
                    style={{ color: palette.blue[300], flexShrink: 0 }}
                  />
                  <Typography color='white' variant='font14'>
                    {t(
                      'previewExecutionMessage.triggeredComponent.dynamicCard',
                    )}
                  </Typography>
                </Row>
                {cards.length > 0 && (
                  <WidgetChipsContainer>
                    {cards.map(card => (
                      <Chip
                        key={card.value}
                        label={getCardTitle(card)}
                        sx={{
                          border: `1px solid ${palette.grey[500]}`,
                          color: 'white',
                        }}
                        variant='outlined'
                      />
                    ))}
                  </WidgetChipsContainer>
                )}
              </ExecutionMessageContainer>
            );
          }
          default:
            return null;
        }
      })}
    </WidgetBatchContainer>
  );
};

export const WorkflowWidgetResponseMessage = ({
  payload,
}: {
  payload: WorkflowWidgetResponsePayload;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const theme = useTheme();
  const palette = theme.palette.colors;

  const { componentType, contextVariables, widgetData } = payload;

  if (componentType === 'buttons') {
    const options = (widgetData?.options ?? []) as Array<{
      display_name: string;
      value: string;
    }>;
    const outputVariable = widgetData?.output_variable as string | undefined;
    const selectedValue = outputVariable
      ? (contextVariables[outputVariable] as string)
      : (Object.values(contextVariables)[0] as string);

    return (
      <ExecutionMessageContainer>
        <Row>
          <Typography color={palette.grey[300]} variant='font14'>
            {t('previewExecutionMessage.userSelected')}
          </Typography>
        </Row>
        <WidgetChipsContainer>
          {options.map(option => {
            const isSelected = option.value === selectedValue;
            return (
              <Chip
                key={option.value}
                label={option.display_name}
                sx={{
                  backgroundColor: isSelected ? palette.blue[700] : undefined,
                  border: isSelected
                    ? 'none'
                    : `1px solid ${palette.grey[500]}`,
                  color: 'white',
                  fontWeight: isSelected ? 500 : undefined,
                }}
                variant={isSelected ? 'filled' : 'outlined'}
              />
            );
          })}
        </WidgetChipsContainer>
      </ExecutionMessageContainer>
    );
  }

  if (componentType === 'form') {
    const formFields = (widgetData?.form_fields ?? []) as Array<{
      output_variable: string;
      placeholder: string;
    }>;
    const getFieldName = (cvId: string): string => {
      const field = formFields.find(f => f.output_variable === cvId);
      return field?.placeholder || cvId;
    };

    return (
      <ExecutionMessageContainer>
        <Row>
          <Typography color={palette.grey[300]} variant='font14'>
            {t('previewExecutionMessage.userSubmittedForm')}
          </Typography>
        </Row>
        {Object.entries(contextVariables).map(([key, value]) => (
          <Typography color='white' key={key} variant='font14'>
            {t('inputCvForm.displayMsgItem', {
              name: getFieldName(key),
              value: String(value),
            })}
          </Typography>
        ))}
      </ExecutionMessageContainer>
    );
  }

  if (componentType === 'dynamic_card') {
    const cards = (widgetData?.cards ?? []) as Array<{
      displayable_dynamic_sections: Array<{
        components: Array<{ label?: string; value: string }>;
        type: string;
      }>;
      value: string;
    }>;
    const outputVariable = widgetData?.output_variable as string | undefined;
    const fallbackOutputVariable = widgetData?.fallback_output_variable as
      | string
      | undefined;
    const fallbackText =
      (widgetData?.fallback_text as string) ||
      t('previewExecutionMessage.fallbackNoneOfThese');

    const selectedValue = outputVariable
      ? (contextVariables[outputVariable] as string)
      : undefined;
    const isFallbackSelected = fallbackOutputVariable
      ? Boolean(contextVariables[fallbackOutputVariable])
      : false;

    return (
      <ExecutionMessageContainer>
        <Row>
          <Typography color={palette.grey[300]} variant='font14'>
            {t('previewExecutionMessage.userSelected')}
          </Typography>
        </Row>
        <WidgetChipsContainer>
          {cards.map(card => {
            const isSelected = card.value === selectedValue;
            return (
              <Chip
                key={card.value}
                label={getCardTitle(card)}
                sx={{
                  backgroundColor: isSelected ? palette.blue[700] : undefined,
                  border: isSelected
                    ? 'none'
                    : `1px solid ${palette.grey[500]}`,
                  color: 'white',
                  fontWeight: isSelected ? 500 : undefined,
                }}
                variant={isSelected ? 'filled' : 'outlined'}
              />
            );
          })}
          {isFallbackSelected && (
            <Chip
              label={fallbackText}
              sx={{
                backgroundColor: palette.blue[700],
                color: 'white',
                fontWeight: 500,
              }}
            />
          )}
        </WidgetChipsContainer>
      </ExecutionMessageContainer>
    );
  }

  return null;
};
